import React, { useState } from 'react'
import './EaseStamp.css'

const EaseStamp = () => {
  const [selectedFiles, setSelectedFiles] = useState([])
  const [processedImages, setProcessedImages] = useState([])
  const [loading, setLoading] = useState(false)
  const [processingIndex, setProcessingIndex] = useState(0)
  const [error, setError] = useState(null)
  const [previewImages, setPreviewImages] = useState([])

  const handleFileChange = e => {
    const files = Array.from(e.target.files)
    if (files.length === 0) return

    setSelectedFiles(files)
    setProcessedImages([])
    setError(null)

    // 创建预览
    const previews = []
    files.forEach((file, index) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        previews[index] = reader.result
        if (previews.length === files.length) {
          setPreviewImages([...previews])
        }
      }
      reader.readAsDataURL(file)
    })
  }

  const addWatermark = async () => {
    if (selectedFiles.length === 0) {
      alert('请选择至少一张图片')
      return
    }

    setLoading(true)
    setError(null)
    setProcessingIndex(0)
    const processed = []

    try {
      // 逐个处理每张图片
      for (let i = 0; i < selectedFiles.length; i++) {
        setProcessingIndex(i + 1)
        const file = selectedFiles[i]

        // 创建 FormData
        const formData = new FormData()
        formData.append('image', file)

        // 调用后端API
        const response = await fetch(
          'https://easestamp-backend.onrender.com/watermark',
          {
            method: 'POST',
            body: formData,
          }
        )

        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(
            `处理第 ${i + 1} 张图片时出错: ${response.status} ${errorText}`
          )
        }

        // 获取返回的图片
        const blob = await response.blob()
        const imageUrl = URL.createObjectURL(blob)
        processed.push({
          url: imageUrl,
          name: file.name,
          index: i,
        })

        // 更新已处理的图片列表
        setProcessedImages([...processed])
      }
    } catch (err) {
      console.error('错误:', err)
      setError(err.message)
    } finally {
      setLoading(false)
      setProcessingIndex(0)
    }
  }

  const downloadAllImages = () => {
    if (processedImages.length === 0) return

    // 逐个下载所有图片
    processedImages.forEach((image, index) => {
      setTimeout(() => {
        const link = document.createElement('a')
        link.href = image.url
        // 生成文件名：原文件名_watermarked.jpg
        const originalName = image.name.replace(/\.[^/.]+$/, '')
        link.download = `${originalName}_watermarked.jpg`
        link.click()
      }, index * 200) // 延迟下载，避免浏览器阻止多个下载
    })
  }

  const downloadSingleImage = imageUrl => {
    const link = document.createElement('a')
    link.href = imageUrl
    link.download = 'watermarked.jpg'
    link.click()
  }

  const reset = () => {
    // 清理所有对象 URL，释放内存
    processedImages.forEach(image => {
      URL.revokeObjectURL(image.url)
    })
    previewImages.forEach(url => {
      URL.revokeObjectURL(url)
    })

    setSelectedFiles([])
    setProcessedImages([])
    setPreviewImages([])
    setError(null)
    // 重置文件输入
    const fileInput = document.getElementById('imageInput')
    if (fileInput) {
      fileInput.value = ''
    }
  }

  return (
    <div className="easestamp-container">
      <div className="easestamp-content">
        <h1 className="easestamp-title">添加时间戳水印</h1>

        <div className="easestamp-upload-section">
          <div className="file-input-wrapper">
            <input
              type="file"
              id="imageInput"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="file-input"
            />
            <label htmlFor="imageInput" className="file-input-label">
              选择图片（可多选）
            </label>
          </div>

          {selectedFiles.length > 0 && (
            <div className="file-info">
              <div className="file-count">
                已选择 {selectedFiles.length} 张图片
              </div>
              <div className="file-list">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="file-item">
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">
                      ({(file.size / 1024).toFixed(2)} KB)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="button-group">
            <button
              onClick={addWatermark}
              disabled={selectedFiles.length === 0 || loading}
              className="btn btn-primary"
            >
              {loading
                ? `处理中... (${processingIndex}/${selectedFiles.length})`
                : `添加时间戳 (${selectedFiles.length} 张)`}
            </button>
            {selectedFiles.length > 0 && (
              <button onClick={reset} className="btn btn-secondary">
                重新选择
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <span>错误: {error}</span>
          </div>
        )}

        <div className="image-preview-section">
          {previewImages.length > 0 && processedImages.length === 0 && (
            <div className="preview-box">
              <h3>原图预览 ({previewImages.length} 张)</h3>
              <div className="image-grid">
                {previewImages.map((preview, index) => (
                  <div key={index} className="preview-item">
                    <img
                      src={preview}
                      alt={`原图 ${index + 1}`}
                      className="preview-image"
                    />
                    <div className="image-label">
                      {selectedFiles[index]?.name || `图片 ${index + 1}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {processedImages.length > 0 && (
            <div className="result-box">
              <h3>
                处理完成！({processedImages.length}/{selectedFiles.length} 张)
              </h3>
              <div className="image-grid">
                {processedImages.map((image, index) => (
                  <div key={index} className="result-item">
                    <img
                      src={image.url}
                      alt={`带时间戳的图片 ${index + 1}`}
                      className="result-image"
                    />
                    <div className="image-label">{image.name}</div>
                    <button
                      onClick={() => downloadSingleImage(image.url)}
                      className="btn btn-download-single"
                    >
                      下载
                    </button>
                  </div>
                ))}
              </div>
              {processedImages.length === selectedFiles.length && (
                <button
                  onClick={downloadAllImages}
                  className="btn btn-download"
                >
                  下载全部 ({processedImages.length} 张)
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default EaseStamp


